import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { quantileLabel } from '@/lib/quantiles';
import { aneelCaseParams, fetchAneelPenalties, fetchAneelPenaltyPosition } from '@/services/aneel';
import type {
  AneelBandLadder,
  AneelNatureLadder,
  AneelPosition,
  AnswerOut,
  QuantileSummary,
} from '@/services/types';

/** Decided notices per regime, read off the band rows the table renders. */
function regimeAttrition(bands: AneelBandLadder[]): { decidedNew: number; decidedOld: number } {
  let decidedNew = 0;
  let decidedOld = 0;
  for (const band of bands) {
    if (band.new_regime) decidedNew += band.decided;
    else decidedOld += band.decided;
  }
  return { decidedNew, decidedOld };
}

/**
 * What each ANEEL penalty band costs, and what an appeal does to it.
 *
 * Most of this screen is refusals, and that is its honest shape: nine of the
 * thirteen bands have too few decided cases to carry a final-penalty ladder. A
 * quantile ladder over seven observations would be a confident-looking answer to
 * a question this corpus cannot support, so those bands say so instead.
 *
 * The two ladders refuse INDEPENDENTLY. A band can hold 923 charges and 53
 * outcomes, so the charge ladder answers while the final ladder cannot —
 * collapsing them into one state would either suppress a well-powered figure or
 * publish an unpowered one.
 */
export function AneelPenaltiesPage() {
  const { t, i18n } = useTranslation(['aneel', 'common']);

  const { data, isPending, error, refetch } = useQuery({
    queryKey: ['aneel', 'penalties'],
    queryFn: fetchAneelPenalties,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  });

  if (isPending) {
    return (
      <div className="space-y-4 p-6">
        <SkeletonLoader rows={2} />
        <SkeletonLoader rows={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      </div>
    );
  }

  const refused = data.bands.filter((band) => band.final.substrate === 'insufficient_sample');
  const attrition = regimeAttrition(data.bands);

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('aneel:penalties.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('aneel:penalties.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="aneel:penalties.explainer" />

      <div className="flex justify-end">
        <ExportButton path="/api/v1/aneel/export/penalties" />
      </div>

      <AsOfStrip asOf={data.as_of} />

      {/* The regime split, above the table. A reader who scans the band column
          sees "Grupo III" twice and reads it as a duplicate label; by the time
          they reach a footnote they have already decided the data is messy. */}
      <section
        role="note"
        className="space-y-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3"
      >
        <h2 className="text-sm font-semibold text-foreground">
          {t('aneel:penalties.regimes.title')}
        </h2>
        <p className="text-xs text-foreground">
          {t('aneel:penalties.regimes.body', {
            newBands: number.format(data.regimes.new_regime_bands),
            newCases: number.format(data.regimes.new_regime_cases),
            oldBands: number.format(data.regimes.old_regime_bands),
            oldCases: number.format(data.regimes.old_regime_cases),
          })}
        </p>
        {/* The attrition between the regimes, summed here from the band rows
            below so the sentence and the table cannot disagree. The newer
            rule's cases are younger, which is why the two decided shares
            differ for a reason that has nothing to do with severity. */}
        {data.regimes.new_regime_cases > 0 && data.regimes.old_regime_cases > 0 ? (
          <p className="text-xs text-muted-foreground" data-regime-attrition>
            {t('aneel:penalties.regimes.attrition', {
              decidedNew: number.format(attrition.decidedNew),
              newCases: number.format(data.regimes.new_regime_cases),
              decidedOld: number.format(attrition.decidedOld),
              oldCases: number.format(data.regimes.old_regime_cases),
            })}
          </p>
        ) : null}
      </section>

      <section className="rounded-lg border bg-card p-4">
        <h2 className="mb-3 text-sm font-semibold text-foreground">
          {t('aneel:penalties.corpus.title')}
        </h2>
        <Answer answer={data.corpus} help={t('aneel:penalties.corpus.help')}>
          {(summary) => <Ladder summary={summary} currency={currency} />}
        </Answer>
      </section>

      <PositionForm
        bands={data.bands.map((band) => band.band)}
        natures={data.natures.map((nature) => nature.nature)}
      />

      <FieldGuide i18nKey="aneel:penalties.guide.ladders" level={2} />

      {/* Stated before the table rather than discovered by scrolling it. That
          nine of thirteen bands cannot answer is the finding, not an omission. */}
      <p className="rounded-md border bg-muted/40 p-3 text-sm text-foreground">
        {t('aneel:penalties.refusedSummary', {
          refused: number.format(refused.length),
          total: number.format(data.bands.length),
        })}
      </p>

      <ul className="space-y-3" data-band-cards>
        {data.bands.map((band) => (
          <BandCard
            // Not `band.band` alone: the same group number appears under both
            // regimes (see the regime-split note above), so two rows can share
            // a label -- the key has to carry what tells them apart.
            key={`${band.band}-${band.new_regime}`}
            band={band}
            currency={currency}
            number={number}
            percent={percent}
          />
        ))}
      </ul>

      <FieldGuide i18nKey="aneel:penalties.guide.band" />

      {/* The conduct dimension beside the bracket dimension. A notice has ONE
          nature, so these rows never count a notice twice the way band rows
          can; the two dimensions are read side by side and never summed. */}
      <section className="space-y-3" data-nature-cards>
        <h2 className="text-sm font-semibold text-foreground">
          {t('aneel:penalties.natures.title')}
        </h2>
        <p className="text-xs text-muted-foreground">
          {t('aneel:penalties.natures.help', {
            shown: number.format(data.natures.length),
            omitted: number.format(data.natures_omitted),
          })}
        </p>
        <ul className="space-y-3">
          {data.natures.map((nature) => (
            <NatureCard
              key={nature.nature}
              nature={nature}
              currency={currency}
              number={number}
              percent={percent}
            />
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * "Where does R$X sit?" -- the question a lawyer holding a charge asks of
 * this screen. Placed among the corpus's CHARGES and, when chosen, a band's
 * and a nature's, through the same populations the ladders are drawn from.
 * Refused below the floor rather than placed among a handful of charges.
 */
function PositionForm({ bands, natures }: { bands: string[]; natures: string[] }) {
  const { t, i18n } = useTranslation(['aneel']);
  const number = new Intl.NumberFormat(i18n.language);
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  const [draft, setDraft] = useState('');
  const [band, setBand] = useState('');
  const [nature, setNature] = useState('');
  const [query, setQuery] = useState<{ amount: number; band: string; nature: string } | null>(
    null,
  );

  const { data, isFetching, error } = useQuery({
    queryKey: ['aneel', 'penalties', 'position', query],
    queryFn: () =>
      fetchAneelPenaltyPosition({
        amount: query!.amount,
        band: query!.band || null,
        nature: query!.nature || null,
      }),
    enabled: query !== null,
  });

  const rank = (share: number) => `p${Math.round(share * 100)}`;
  const line = (label: string, answer: AnswerOut<AneelPosition>) => (
    <div className="rounded-md border bg-background p-3" data-position={label}>
      <h3 className="mb-1 text-xs font-medium text-foreground">{label}</h3>
      <Answer answer={answer} help={t('aneel:penalties.position.help')} scope="record">
        {(position) => (
          <p className="text-sm text-foreground">
            {t('aneel:penalties.position.result', {
              amount: currency.format(position.amount),
              percentile: rank(position.percentile),
              n: number.format(position.n),
            })}
          </p>
        )}
      </Answer>
    </div>
  );

  return (
    <section className="space-y-3 rounded-lg border bg-card p-4" data-position-form>
      <h2 className="text-sm font-semibold text-foreground">{t('aneel:penalties.position.title')}</h2>
      <p className="text-xs text-muted-foreground">{t('aneel:penalties.position.intro')}</p>
      <form
        className="flex flex-wrap items-end gap-3"
        onSubmit={(event) => {
          event.preventDefault();
          // Digits only: the reader types a BRL figure with or without
          // separators, and anything else is not an amount.
          const amount = Number(draft.replace(/[^\d]/g, ''));
          if (amount > 0) setQuery({ amount, band, nature });
        }}
      >
        <div className="space-y-1">
          <label htmlFor="aneel-position-amount" className="block text-xs font-medium text-foreground">
            {t('aneel:penalties.position.amount')}
          </label>
          <input
            id="aneel-position-amount"
            type="text"
            inputMode="numeric"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder="R$"
            className="w-40 rounded-md border bg-background px-3 py-1.5 text-sm tabular-nums text-foreground"
          />
        </div>
        <div className="space-y-1">
          <label htmlFor="aneel-position-band" className="block text-xs font-medium text-foreground">
            {t('aneel:penalties.position.band')}
          </label>
          <select
            id="aneel-position-band"
            value={band}
            onChange={(event) => setBand(event.target.value)}
            className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('aneel:penalties.position.none')}</option>
            {bands.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label htmlFor="aneel-position-nature" className="block text-xs font-medium text-foreground">
            {t('aneel:penalties.position.nature')}
          </label>
          <select
            id="aneel-position-nature"
            value={nature}
            onChange={(event) => setNature(event.target.value)}
            className="max-w-[16rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
          >
            <option value="">{t('aneel:penalties.position.none')}</option>
            {natures.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>
        <button
          type="submit"
          className="rounded-md border bg-foreground px-3 py-1.5 text-sm font-medium text-background"
        >
          {t('aneel:penalties.position.submit')}
        </button>
      </form>
      {error ? <ErrorMessage message={error.message} /> : null}
      {isFetching ? <SkeletonLoader rows={2} /> : null}
      {data && !isFetching ? (
        <div className="grid gap-3 lg:grid-cols-3" data-position-results>
          {line(t('aneel:penalties.position.corpus'), data.corpus)}
          {data.band && data.in_band ? line(data.band, data.in_band) : null}
          {data.nature && data.in_nature ? line(data.nature, data.in_nature) : null}
        </div>
      ) : null}
      <p className="text-xs text-muted-foreground">{t('aneel:penalties.position.reading')}</p>
    </section>
  );
}

function NatureCard({
  nature,
  currency,
  number,
  percent,
}: {
  nature: AneelNatureLadder;
  currency: Intl.NumberFormat;
  number: Intl.NumberFormat;
  percent: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <li className="rounded-lg border bg-card" data-nature-card={nature.nature}>
      <details className="group">
        <summary className="flex cursor-pointer flex-wrap items-baseline justify-between gap-2 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden
            />
            {nature.nature}
          </h3>
          <span className="text-xs text-muted-foreground">
            {t('aneel:penalties.bandCounts', {
              cases: number.format(nature.cases),
              decided: number.format(nature.decided),
              share: percent.format(nature.decided_share),
            })}
          </span>
        </summary>
        <div className="space-y-3 px-4 pb-4">
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-md border bg-background p-3">
              <h4 className="mb-2 text-xs font-medium text-foreground">
                {t('aneel:penalties.charged')}
              </h4>
              <Answer answer={nature.charged} help={t('aneel:penalties.chargedHelp')}>
                {(summary) => <Ladder summary={summary} currency={currency} />}
              </Answer>
            </div>
            <div className="rounded-md border bg-background p-3">
              <h4 className="mb-2 text-xs font-medium text-foreground">
                {t('aneel:penalties.final')}
              </h4>
              <Answer answer={nature.final} help={t('aneel:penalties.finalHelp')}>
                {(summary) => <Ladder summary={summary} currency={currency} />}
              </Answer>
            </div>
          </div>
          <Link
            to={`/aneel/cases?${aneelCaseParams({ nature: nature.nature }).toString()}`}
            className="text-xs text-primary underline-offset-4 hover:underline"
          >
            {t('aneel:penalties.openCases')}
          </Link>
        </div>
      </details>
    </li>
  );
}

function BandCard({
  band,
  currency,
  number,
  percent,
}: {
  band: AneelBandLadder;
  currency: Intl.NumberFormat;
  number: Intl.NumberFormat;
  percent: Intl.NumberFormat;
}) {
  const { t } = useTranslation(['aneel']);

  return (
    <li className="rounded-lg border bg-card" data-band-card={band.band}>
      {/* Collapsible: thirteen bands times two ladders is a long page, and a
          reader usually wants one. Open by default only where a final ladder
          exists, so the answered bands are the ones a scan meets first. */}
      <details
        className="group"
        open={band.final.substrate === 'available' || band.final.substrate === 'partial'}
      >
        <summary className="flex cursor-pointer flex-wrap items-baseline justify-between gap-2 p-4">
          <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
            <ChevronDown
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform group-open:rotate-180"
              aria-hidden
            />
            {band.band}
          </h3>
          <span className="flex items-baseline gap-2">
            <span className="text-xs text-muted-foreground">
              {t('aneel:penalties.bandCounts', {
                cases: number.format(band.cases),
                decided: number.format(band.decided),
                share: percent.format(band.decided_share),
              })}
            </span>
            {/* The regime, on every row. It is the difference between two rows
                that otherwise read as the same band listed twice. */}
            <span className="rounded border px-1.5 py-0.5 text-[0.65rem] text-muted-foreground">
              {band.new_regime
                ? t('aneel:penalties.regimes.new')
                : t('aneel:penalties.regimes.old')}
            </span>
          </span>
        </summary>
        <div className="space-y-3 px-4 pb-4">
          <div className="grid gap-3 lg:grid-cols-2">
            <div className="rounded-md border bg-background p-3">
              <h4 className="mb-2 text-xs font-medium text-foreground">
                {t('aneel:penalties.charged')}
              </h4>
              <Answer answer={band.charged} help={t('aneel:penalties.chargedHelp')}>
                {(summary) => <Ladder summary={summary} currency={currency} />}
              </Answer>
            </div>
            <div className="rounded-md border bg-background p-3">
              <h4 className="mb-2 text-xs font-medium text-foreground">
                {t('aneel:penalties.final')}
              </h4>
              <Answer answer={band.final} help={t('aneel:penalties.finalHelp')}>
                {(summary) => <Ladder summary={summary} currency={currency} />}
              </Answer>
            </div>
          </div>
          <Link
            to={`/aneel/cases?${aneelCaseParams({ band: band.band }).toString()}`}
            className="text-xs text-primary underline-offset-4 hover:underline"
          >
            {t('aneel:penalties.openCases')}
          </Link>
        </div>
      </details>
    </li>
  );
}

/**
 * A quantile ladder as a definition list, not a chart.
 *
 * Thirteen bands times two ladders is twenty-six charts, and a reader comparing
 * two bands would be comparing two independently-scaled axes. Numbers in a fixed
 * order compare cleanly and carry their own labels.
 */
function Ladder({
  summary,
  currency,
}: {
  summary: QuantileSummary;
  currency: Intl.NumberFormat;
}) {
  const { t, i18n } = useTranslation(['aneel']);
  const count = new Intl.NumberFormat(i18n.language);

  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        {t('aneel:penalties.observations', { n: count.format(summary.n) })}
      </p>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-5">
        {Object.entries(summary.quantiles).map(([key, value]) => (
          <div key={key}>
            <dt className="text-muted-foreground">{quantileLabel(t, key)}</dt>
            <dd className="tabular-nums font-medium text-foreground">{currency.format(value)}</dd>
          </div>
        ))}
      </dl>
      {/* The extremes, beside the ladder: the smallest and the largest charge
          the population actually holds, which the quantiles never reach. */}
      <p className="text-xs text-muted-foreground" data-extremes>
        {t('aneel:penalties.extremes', {
          minimum: currency.format(summary.minimum),
          maximum: currency.format(summary.maximum),
        })}
      </p>
    </div>
  );
}
